import React, { useState } from "react";
import { Modal, Row, Col, Alert, Badge, Button } from "reactstrap";
import { useTheme } from "../contexts/ThemeContext";

const VpsErrorsModal = ({
  showVpsErrors,
  setShowVpsErrors,
  vpsErrorMessages,
  isViewTxn = false,
  setAuthWithWarnings = () => {},
  setAuthModal = () => {},
  pageFlag = "compliance",
}: {
  showVpsErrors: any;
  setShowVpsErrors: any;
  vpsErrorMessages: any;
  isViewTxn?: boolean;
  setAuthModal?: any;
  setAuthWithWarnings?: any;
  pageFlag?: "bulk" | "compliance" | string;
}) => {
  const { theme } = useTheme();
  const [selectedBulkTxn, setSelectedBulkTxn] = useState<any>(null);

  // Theme colors with proper dark mode support
  const themeColors = {
    bgPrimary: theme === "dark" ? "#1e1e2f" : "#ffffff",
    bgCard: theme === "dark" ? "#27293d" : "#f8f9fa",
    textPrimary: theme === "dark" ? "#ececec" : "#333333",
    textSecondary: theme === "dark" ? "#e0e0e0" : "#6c757d",
    borderColor: theme === "dark" ? "#404040" : "#dee2e6",
    hintBorder: theme === "dark" ? "#45b7d1" : "#17a2b8",
    badge: {
      dangerBg: "#dc3545",
      primaryBg: "#007bff",
    },
  };

  const fontSize = {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    md: "1.125rem",
    lg: "1.25rem",
  };

  const formatCurrency = (value: number) => {
    if (value === undefined || value === null) return "—";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatUnits = (value: number) => {
    if (value === undefined || value === null) return "—";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  };

  const DataItem = ({
    label,
    value,
    color,
  }: {
    label: string;
    value: any;
    color?: string;
  }) => (
    <div
      className="d-flex justify-content-between align-items-center mb-1 py-2 border-bottom"
      style={{ fontSize: fontSize.sm, borderColor: themeColors.borderColor }}
    >
      <span
        className="font-weight-bold"
        style={{ color: themeColors.textSecondary }}
      >
        {label}
      </span>
      <span
        style={{
          color: color || themeColors.textPrimary,
          fontWeight: 600,
          fontSize: fontSize.base,
        }}
      >
        {value}
      </span>
    </div>
  );

  const renderErrorContent = (error: any) => {
    const isCoa = !error.error_type;
    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <Badge
            color={isCoa ? "primary" : "danger"}
            pill
            style={{ fontSize: fontSize.xs }}
          >
            {isCoa ? "CoA" : (error.error_type || "ERROR").replace(/_/g, " ")}
          </Badge>
          <span
            className="font-weight-bold"
            style={{ color: "#45b7d1", fontSize: fontSize.sm }}
          >
            {error.fund_code}
          </span>
        </div>

        {error.error && (
          <p
            className="small font-italic mb-2"
            style={{ color: "#ff6b6b", fontSize: fontSize.sm }}
          >
            {error.error}
          </p>
        )}

        <div
          className="p-3 rounded shadow-sm"
          style={{
            backgroundColor: themeColors.bgCard,
            border: `1px solid ${themeColors.borderColor}`,
          }}
        >
          {error.requested !== undefined && (
            <DataItem
              label="Requested"
              value={formatCurrency(error.requested)}
              color="#ff6b6b"
            />
          )}
          {error.available !== undefined && (
            <DataItem
              label="Available"
              value={formatCurrency(error.available)}
              color="#51cf66"
            />
          )}
          {error.requested_units !== undefined && (
            <DataItem
              label="Req. Units"
              value={formatUnits(error.requested_units)}
              color="#ff6b6b"
            />
          )}
          {error.available_units !== undefined && (
            <DataItem
              label="Avail. Units"
              value={formatUnits(error.available_units)}
              color="#51cf66"
            />
          )}
          {error.provided_units !== undefined && (
            <DataItem
              label="Req. Units"
              value={formatUnits(error.provided_units)}
              color="#ff6b6b"
            />
          )}
          {error.expected_units !== undefined && (
            <DataItem
              label="Avail. Units"
              value={formatUnits(error.expected_units)}
              color="#51cf66"
            />
          )}
          {error.provided_amount !== undefined && (
            <DataItem
              label="Provided"
              value={formatCurrency(error.provided_amount)}
            />
          )}
          {error.expected_amount !== undefined && (
            <DataItem
              label="Expected"
              value={formatCurrency(error.expected_amount)}
              color="#51cf66"
            />
          )}
          {error.nav !== undefined && (
            <DataItem label="NAV" value={Number(error.nav).toFixed(4)} />
          )}

          {/* CoA / conversion-specific fields */}
          {error.provided_price !== undefined && (
            <DataItem
              label="Provided Price"
              value={formatCurrency(error.provided_price)}
              color="#ff6b6b"
            />
          )}
          {error.db_price !== undefined && (
            <DataItem
              label="System Price"
              value={formatCurrency(error.db_price)}
              color="#51cf66"
            />
          )}
          {error.nav_date !== undefined && (
            <DataItem label="NAV Date" value={error.nav_date} />
          )}
          {error.conversion !== undefined && (
            <DataItem label="Conversion" value={error.conversion} />
          )}
          {error.totalPctRequested !== undefined && (
            <DataItem
              label="Total % Requested"
              value={`${error.totalPctRequested}%`}
              color="#ff6b6b"
            />
          )}
          {error.currentProportion !== undefined && (
            <DataItem
              label="Current DB Proportion"
              value={`${error.currentProportion}%`}
              color="#51cf66"
            />
          )}
          {error.requestedPct !== undefined && (
            <DataItem
              label="Requested %"
              value={`${error.requestedPct}%`}
              color="#ff6b6b"
            />
          )}
          {error.remainingProportion !== undefined && (
            <DataItem
              label="Remaining Proportion %"
              value={`${error.remainingProportion}%`}
              color={error.remainingProportion < 0 ? "#ff6b6b" : "#51cf66"}
            />
          )}
          {error.alreadyAllocated !== undefined && (
            <DataItem
              label="Proportion Allocated"
              value={`${error.alreadyAllocated}%`}
            />
          )}
        </div>

        {error.hint && (
          <div
            className="mt-3 p-2 font-italic"
            style={{
              borderLeft: `3px solid ${themeColors.hintBorder}`,
              backgroundColor:
                theme === "dark"
                  ? "rgba(69, 183, 209, 0.1)"
                  : "rgba(69, 183, 209, 0.05)",
              color: themeColors.textSecondary,
              fontSize: fontSize.sm,
            }}
          >
            {error.hint}
          </div>
        )}
        {error.message && (
          <div
            className="mt-3 p-2 font-italic"
            style={{
              borderLeft: `3px solid ${themeColors.hintBorder}`,
              backgroundColor:
                theme === "dark"
                  ? "rgba(69, 183, 209, 0.1)"
                  : "rgba(69, 183, 209, 0.05)",
              color: themeColors.textSecondary,
              fontSize: fontSize.sm,
            }}
          >
            {error.message}
          </div>
        )}
      </div>
    );
  };

  // ── BULK: txn listing view ──────────────────────────────────────────────────
  const renderBulkList = () => (
    <div style={{ maxHeight: "65vh", overflowY: "auto", padding: "8px" }}>
      {vpsErrorMessages && vpsErrorMessages.length > 0 ? (
        vpsErrorMessages.map((txn: any, idx: number) => {
          const errorCount = txn.messages?.length ?? 0;
          const navErrors =
            txn.messages?.filter((m: any) => m.error_type === "nav_mismatch")
              .length ?? 0;
          const allocErrors =
            txn.messages?.filter((m: any) => m.error_type === "allocation")
              .length ?? 0;

          return (
            <div
              key={txn.txn_id ?? idx}
              className="d-flex align-items-center justify-content-between px-3 py-3 mb-2 rounded"
              style={{
                border: `1px solid ${themeColors.borderColor}`,
                backgroundColor: themeColors.bgCard,
              }}
            >
              {/* Left: txn info */}
              <div
                className="d-flex align-items-center"
                style={{ gap: "12px", flexWrap: "wrap" }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: fontSize.sm,
                    color: themeColors.textPrimary,
                  }}
                >
                  Txn&nbsp;
                  <span style={{ color: "#45b7d1" }}>
                    {txn.txn_id ?? `#${idx + 1}`}
                  </span>
                </span>

                <Badge color="danger" pill style={{ fontSize: fontSize.xs }}>
                  {errorCount} error{errorCount !== 1 ? "s" : ""}
                </Badge>

                {navErrors > 0 && (
                  <Badge
                    style={{
                      fontSize: fontSize.xs,
                      backgroundColor: "#fd7e14",
                      color: "#fff",
                    }}
                    pill
                  >
                    {navErrors} NAV mismatch{navErrors !== 1 ? "es" : ""}
                  </Badge>
                )}
                {allocErrors > 0 && (
                  <Badge
                    style={{
                      fontSize: fontSize.xs,
                      backgroundColor: "#6f42c1",
                      color: "#fff",
                    }}
                    pill
                  >
                    {allocErrors} allocation issue{allocErrors !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>

              {/* Right: action */}
              <Button
                size="sm"
                color="danger"
                outline
                style={{ fontSize: fontSize.xs, whiteSpace: "nowrap" }}
                onClick={() => setSelectedBulkTxn(txn)}
              >
                <i className="fa fa-eye mr-1" />
                View Errors
              </Button>
            </div>
          );
        })
      ) : (
        <p
          className="text-center py-5"
          style={{ color: themeColors.textSecondary }}
        >
          No bulk error records found.
        </p>
      )}
    </div>
  );

  // ── BULK: individual txn error detail view ─────────────────────────────────
  const renderBulkDetail = () => {
    const messages = selectedBulkTxn?.messages ?? [];
    return (
      <>
        {/* Back button */}
        <div className="mb-3">
          <Button
            size="sm"
            color="secondary"
            outline
            style={{ fontSize: fontSize.sm }}
            onClick={() => setSelectedBulkTxn(null)}
          >
            <i className="fa fa-arrow-left mr-1" />
            Back to List
          </Button>
          <span
            className="ml-3 font-weight-bold"
            style={{ color: "#45b7d1", fontSize: fontSize.sm }}
          >
            Txn ID: {selectedBulkTxn?.txn_id}
          </span>
        </div>

        <div
          style={{
            maxHeight: "58vh",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "4px",
          }}
        >
          <Row>
            {messages.length > 0 ? (
              messages.map((error: any, index: number) => (
                <Col md="6" key={index} className="mb-3">
                  <div
                    style={{
                      border: `1px solid ${themeColors.borderColor}`,
                      borderRadius: "8px",
                      backgroundColor: themeColors.bgCard,
                      color: themeColors.textPrimary,
                      height: "100%",
                    }}
                  >
                    {renderErrorContent(error)}
                  </div>
                </Col>
              ))
            ) : (
              <Col className="text-center py-5">
                <p
                  style={{
                    color: themeColors.textSecondary,
                    fontSize: fontSize.base,
                  }}
                >
                  No detailed error logs found for this transaction.
                </p>
              </Col>
            )}
          </Row>
        </div>
      </>
    );
  };

  // ── COMPLIANCE: original flat error grid ───────────────────────────────────
  const renderComplianceErrors = () => (
    <div
      style={{
        maxHeight: "65vh",
        overflowY: "auto",
        overflowX: "hidden",
        padding: "8px",
      }}
    >
      <Row>
        {vpsErrorMessages && vpsErrorMessages.length > 0 ? (
          vpsErrorMessages
            .flatMap((g: any) =>
              g.messages && Array.isArray(g.messages) ? g.messages : g,
            )
            .map((error: any, index: number) => (
              <Col md="6" key={index} className="mb-3">
                <div
                  style={{
                    border: `1px solid ${themeColors.borderColor}`,
                    borderRadius: "8px",
                    backgroundColor: themeColors.bgCard,
                    color: themeColors.textPrimary,
                    height: "100%",
                  }}
                >
                  {renderErrorContent(error)}
                </div>
              </Col>
            ))
        ) : (
          <Col className="text-center py-5">
            <p
              style={{
                color: themeColors.textSecondary,
                fontSize: fontSize.base,
              }}
            >
              No detailed error logs found.
            </p>
          </Col>
        )}
      </Row>
    </div>
  );

  if (!showVpsErrors) return null;

  // Derive modal title based on mode + drill-down state
  const modalTitle =
    pageFlag === "bulk"
      ? selectedBulkTxn
        ? `Errors — Txn ${selectedBulkTxn.txn_id}`
        : "Bulk Upload — Validation Errors"
      : "VPS Validation Summary";

  return (
    <Modal
      isOpen={showVpsErrors}
      toggle={() => {
        setSelectedBulkTxn(null);
        setShowVpsErrors(false);
      }}
      size="xl"
      centered
      style={{ color: themeColors.textPrimary }}
    >
      {/* Header */}
      <div
        className="modal-header py-3"
        style={{
          backgroundColor: "#dc3545",
          borderBottom: `1px solid ${themeColors.borderColor}`,
        }}
      >
        <h5
          className="modal-title m-0"
          style={{ color: "#ffffff", fontSize: fontSize.lg }}
        >
          <i className="fa fa-exclamation-triangle mr-2" />
          {modalTitle}
        </h5>
        <button
          className="close"
          style={{ color: "#ffffff" }}
          onClick={() => {
            setSelectedBulkTxn(null);
            setShowVpsErrors(false);
          }}
        >
          &times;
        </button>
      </div>

      {/* Body */}
      <div
        className="modal-body"
        style={{
          backgroundColor: themeColors.bgPrimary,
          color: themeColors.textPrimary,
          fontSize: fontSize.base,
        }}
      >
        <Alert
          color={isViewTxn ? "warning" : "danger"}
          className="py-2 mb-3"
          style={{ fontSize: fontSize.sm }}
        >
          {pageFlag === "bulk"
            ? selectedBulkTxn
              ? `Showing validation errors for transaction ${selectedBulkTxn.txn_id}.`
              : `${vpsErrorMessages?.length ?? 0} transaction(s) failed validation. Click "View Errors" to inspect each one.`
            : isViewTxn
              ? "Validation warnings found. Review data before authorization."
              : "Errors detected. Records have been updated with validation flags."}
        </Alert>

        {pageFlag === "bulk"
          ? selectedBulkTxn
            ? renderBulkDetail()
            : renderBulkList()
          : renderComplianceErrors()}
      </div>

      {/* Footer */}
      <div
        className="modal-footer py-3"
        style={{
          backgroundColor: themeColors.bgPrimary,
          borderTop: `1px solid ${themeColors.borderColor}`,
        }}
      >
        {isViewTxn && pageFlag !== "bulk" && (
          <button
            className="btn btn-sm btn-primary"
            style={{ fontSize: fontSize.sm }}
            onClick={() => {
              setShowVpsErrors(false);
              setAuthWithWarnings(true);
              setAuthModal(true);
            }}
          >
            Authorize Anyway
          </button>
        )}
        <button
          className="btn btn-sm btn-secondary"
          style={{ fontSize: fontSize.sm }}
          onClick={() => {
            setSelectedBulkTxn(null);
            setShowVpsErrors(false);
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default VpsErrorsModal;
